import { useSearchParams } from 'react-router-dom';
import BannerFilter, { isValidFilter } from './components/filter';
import { BannerNotificationFilter } from './constants/banner-notification-filter.enum';
import { usePagination } from '../../hooks/use-pagination';
import { useCallback, useEffect, useState } from 'react';
import useApiRequest from '../../lib/hooks/useRequest';
import { Paginated } from '../../lib/interfaces/utils';
import { BannerNotificationListFullResponseDto } from './types/response';
import Preloader from '../../ui/components/ui/preloader';
import DataFetchParent from '../../ui/components/ui/data-fetch';
import BannerDataTable from './components/data-table';
import BannerFormDialog from './components/banner-form-dialog';
import { Button } from '../../ui/components/ui/button';
import AddIcon from '../../ui/components/icons/add-icon';

const BannerPage = () => {
  const [searchParams] = useSearchParams();

  const { apiRequest } = useApiRequest();
  const { pagination, isValidPagination, handlePaginationChange, resetPaginationOnSearchParamsChange } = usePagination({
    pageSize: 10,
    pageParamName: 'page'
  });

  const isReady = isValidPagination && isValidFilter(searchParams.get('filter'));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const [bannerNotifications, setBannerNotifications] = useState<BannerNotificationListFullResponseDto[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);

  const fetchBannerNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const baseQuery = searchParams.toString();
      const searchQuery = `${baseQuery ? `${baseQuery}&` : ''}limit=${pagination.pageSize}`;

      const response = await apiRequest<Paginated<BannerNotificationListFullResponseDto>>(`notifications/banner/list?${searchQuery}`, 'GET');
      if (response.data) {
        setBannerNotifications(response.data.docs);
        setPageCount(response.data.pageCount);
      }
    } catch (error) {
      console.error('Error fetching banner notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, searchParams, pagination.pageSize]);

  useEffect(() => {
    if (!isReady) return;
    fetchBannerNotifications();
  }, [fetchBannerNotifications, isReady]);

  const onRefetch = useCallback(async () => {
    await fetchBannerNotifications();
  }, [fetchBannerNotifications]);

  const handleCreate = async (data: { message: string; dateStart: string; dateEnd: string }) => {
    await apiRequest('notifications/banner', 'POST', {
      body: data,
      toast: {
        toastText: 'Banner wurde erstellt.'
      }
    });
    await onRefetch();
  };

  return (
    <>
      <div className="flex justify-between">
        <BannerFilter
          paramName="filter"
          defaultValue={BannerNotificationFilter.CURRENT}
          onFilterSearchParamsChange={(newSearchParams) => {
            return resetPaginationOnSearchParamsChange(newSearchParams);
          }}
        />
        <div className="flex gap-3">
          <Button className="flex gap-4" onClick={() => setIsCreateDialogOpen(true)}>
            <AddIcon />
            <p>Banner erstellen</p>
          </Button>
        </div>
      </div>
      {/* Content */}
      {isReady ? (
        <DataFetchParent
          data={bannerNotifications}
          renderElement={(data: BannerNotificationListFullResponseDto[]) => (
            <BannerDataTable
              data={data}
              setPagination={handlePaginationChange}
              pageCount={pageCount}
              pagination={pagination}
              isLoading={isLoading}
              onRefetch={onRefetch}
            />
          )}
          isLoading={isLoading}
        />
      ) : (
        <Preloader />
      )}
      <BannerFormDialog isOpen={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} onSubmit={handleCreate} />
    </>
  );
};

export default BannerPage;
